import classNames from 'classnames'
import * as dateFns from 'date-fns'
import React, { PropsWithChildren } from 'react'

import { Icon } from '../../../components'
import { formatDollar, shortFormatDollar } from '../../../util'
import { EarningsEstimateType } from '../../earnings-common/types'
import { EARNINGS_DATE_FORMAT, EarningsColor, EarningsEstimateTypeLabels } from '../constants'
import { EarningRevisionWithDate } from '../types'
import { formatFiscalName, getEarningsColor } from '../utils'

function formatEstimateLabel(estimateType: EarningsEstimateType, fiscalPeriod: string) {
  return `${formatFiscalName(fiscalPeriod)} ${EarningsEstimateTypeLabels[estimateType]} Estimate`
}

enum TooltipIcon {
  dot = 'dot',
  downArrow = 'downArrow',
  upArrow = 'upArrow',
}

type TooltipRowProps = {
  color: string
  icon: TooltipIcon
  name: string
}

function TooltipRow({ color, icon, name, children }: PropsWithChildren<TooltipRowProps>) {
  return (
    <div>
      <div key={name} className={classNames('flex items-center justify-between space-x-2.5')}>
        <span className="w-38 flex items-center space-x-1.5">
          {icon === TooltipIcon.upArrow && (
            <Icon width={12} name="arrowUpShort" className="text-green-500 dark:text-green-400" />
          )}
          {icon === TooltipIcon.downArrow && (
            <Icon width={12} name="arrowDownShort" className="text-red-500 dark:text-red-400" />
          )}
          {icon === TooltipIcon.dot && (
            <span className="ml-[3px] block h-1.5 w-1.5 rounded-full" style={{ backgroundColor: color }}></span>
          )}
          <span>{name}</span>
        </span>
        <span className="text-nowrap text-right">{children}</span>
      </div>
    </div>
  )
}

type Props = {
  estimateType: EarningsEstimateType
  isActive: boolean
  revision: EarningRevisionWithDate | undefined
}

export function PriceRevisionTooltip({ estimateType, isActive, revision }: Props) {
  if (!estimateType || !isActive || !revision) return null

  return (
    <div className="relative -top-8 space-y-1.5 rounded-md border border-secondary bg-primary bg-opacity-70 p-2.5 font-sans text-2xs leading-4 text-muted-2">
      <div className="text-sm text-muted font-medium">
        {revision.date && dateFns.format(revision.date, EARNINGS_DATE_FORMAT)}
      </div>
      <TooltipRow
        color={getEarningsColor(EarningsColor.estimate)}
        icon={TooltipIcon.dot}
        name={formatEstimateLabel(estimateType, revision.fiscalPeriod)}
      >
        {shortFormatDollar(revision.mean)} ({shortFormatDollar(revision.low)}–{shortFormatDollar(revision.high)})
      </TooltipRow>
      <TooltipRow color={getEarningsColor(EarningsColor.price)} icon={TooltipIcon.dot} name="Price">
        {formatDollar(revision.price)}
      </TooltipRow>
      <TooltipRow color={getEarningsColor(EarningsColor.price)} icon={TooltipIcon.upArrow} name="Up Revisions">
        {revision.upRevisions}/{revision.estimates}
      </TooltipRow>
      <TooltipRow color={getEarningsColor(EarningsColor.price)} icon={TooltipIcon.downArrow} name="Down Revisions">
        {revision.downRevisions}/{revision.estimates}
      </TooltipRow>
    </div>
  )
}
